// src/components/vendor/VendorGoals.tsx
// Sistema de Metas do Vendedor

import React, { useState, useEffect, useCallback } from 'react';
import {
    Target,
    TrendingUp,
    DollarSign,
    ShoppingCart,
    Plus,
    Edit2,
    Trash2,
    Calendar,
    CheckCircle,
    XCircle,
    AlertCircle
} from 'lucide-react';
import './VendorGoals.css';

interface Goal {
    _id: string;
    type: 'revenue' | 'commission' | 'sales';
    targetValue: number;
    currentValue: number;
    progress: number;
    status: 'active' | 'achieved' | 'failed';
    month: number;
    year: number;
}

interface HistoryMonth {
    month: number;
    year: number;
    goals: Goal[];
}

interface VendorGoalsProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const VendorGoals: React.FC<VendorGoalsProps> = ({ onShowToast }) => {
    const [goals, setGoals] = useState<Goal[]>([]);
    const [history, setHistory] = useState<HistoryMonth[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);

    // Estados do formulário
    const [goalType, setGoalType] = useState<'revenue' | 'commission' | 'sales'>('revenue');
    const [targetValue, setTargetValue] = useState('');

    const loadGoals = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');

            const response = await fetch('/api/vendor/goals', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setGoals(data.goals || []);
            } else {
                onShowToast('Erro ao carregar metas', 'error');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao carregar metas', 'error');
        } finally {
            setLoading(false);
        }
    }, [onShowToast]);

    useEffect(() => {
        loadGoals();
        loadHistory();
    }, [loadGoals]);

    const loadHistory = async () => {
        try {
            const token = localStorage.getItem('accessToken');

            const response = await fetch('/api/vendor/goals/history?months=6', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setHistory(data.history || []);
            }
        } catch (error) {
            console.error('Erro ao carregar histórico:', error);
        }
    };

    const handleCreateGoal = async () => {
        if (!targetValue || parseFloat(targetValue) <= 0) {
            onShowToast('Valor da meta é obrigatório', 'error');
            return;
        }

        try {
            const token = localStorage.getItem('accessToken');

            // Converter para centavos se for revenue ou commission
            let valueToSend = parseFloat(targetValue);
            if (goalType === 'revenue' || goalType === 'commission') {
                valueToSend = Math.round(valueToSend * 100); // Converter para centavos
            }

            const response = await fetch('/api/vendor/goals', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    type: goalType,
                    targetValue: valueToSend
                })
            });

            if (response.ok) {
                onShowToast('Meta criada com sucesso!', 'success');
                setShowCreateModal(false);
                setTargetValue('');
                loadGoals();
            } else {
                const error = await response.json();
                onShowToast(error.message || 'Erro ao criar meta', 'error');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao criar meta', 'error');
        }
    };

    const handleUpdateGoal = async () => {
        if (!selectedGoal || !targetValue || parseFloat(targetValue) <= 0) {
            onShowToast('Valor da meta é obrigatório', 'error');
            return;
        }

        try {
            const token = localStorage.getItem('accessToken');

            let valueToSend = parseFloat(targetValue);
            if (selectedGoal.type === 'revenue' || selectedGoal.type === 'commission') {
                valueToSend = Math.round(valueToSend * 100);
            }

            const response = await fetch(`/api/vendor/goals/${selectedGoal._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ targetValue: valueToSend })
            });

            if (response.ok) {
                onShowToast('Meta atualizada!', 'success');
                setShowEditModal(false);
                setSelectedGoal(null);
                setTargetValue('');
                loadGoals();
            } else {
                const error = await response.json();
                onShowToast(error.message || 'Erro ao atualizar meta', 'error');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao atualizar meta', 'error');
        }
    };

    const handleDeleteGoal = async (goalId: string) => {
        if (!window.confirm('Tem certeza que deseja deletar esta meta?')) {
            return;
        }

        try {
            const token = localStorage.getItem('accessToken');

            const response = await fetch(`/api/vendor/goals/${goalId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                onShowToast('Meta deletada!', 'success');
                loadGoals();
            } else {
                const error = await response.json();
                onShowToast(error.message || 'Erro ao deletar meta', 'error');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao deletar meta', 'error');
        }
    };

    const openEditModal = (goal: Goal) => {
        setSelectedGoal(goal);

        // Converter de centavos para reais se necessário
        if (goal.type === 'revenue' || goal.type === 'commission') {
            setTargetValue((goal.targetValue / 100).toFixed(2));
        } else {
            setTargetValue(goal.targetValue.toString());
        }

        setShowEditModal(true);
    };

    const formatCurrency = (cents: number) => {
        return `R$ ${(cents / 100).toFixed(2)}`;
    };

    const getGoalIcon = (type: string) => {
        switch (type) {
            case 'revenue':
                return <TrendingUp size={24} />;
            case 'commission':
                return <DollarSign size={24} />;
            case 'sales':
                return <ShoppingCart size={24} />;
            default:
                return <Target size={24} />;
        }
    };

    const getGoalLabel = (type: string) => {
        switch (type) {
            case 'revenue':
                return 'Receita Total';
            case 'commission':
                return 'Comissão';
            case 'sales':
                return 'Volume de Vendas';
            default:
                return type;
        }
    };

    const getGoalColor = (type: string) => {
        switch (type) {
            case 'revenue':
                return '#3b82f6';
            case 'commission':
                return '#2F7130';
            case 'sales':
                return '#f59e0b';
            default:
                return '#6B7280';
        }
    };

    const formatValue = (type: string, value: number) => {
        if (type === 'revenue' || type === 'commission') {
            return formatCurrency(value);
        }
        return `${value} vendas`;
    };

    const getMonthName = (month: number) => {
        const months = [
            'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
            'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
        ];
        return months[month - 1];
    };

    const hasGoalOfType = (type: string) => {
        return goals.some(g => g.type === type);
    };

    if (loading) {
        return (
            <div className="goals-loading">
                <div className="spinner"></div>
                <p>Carregando metas...</p>
            </div>
        );
    }

    return (
        <div className="vendor-goals">
            <div className="goals-header">
                <div>
                    <h2>Metas do Mês</h2>
                    <p className="goals-subtitle">Defina suas metas mensais e acompanhe seu progresso</p>
                </div>
                <button
                    className="btn-create-goal"
                    onClick={() => setShowCreateModal(true)}
                    disabled={goals.length >= 3}
                >
                    <Plus size={18} />
                    Nova Meta
                </button>
            </div>

            {/* Cards de Metas */}
            {goals.length === 0 ? (
                <div className="goals-empty">
                    <Target size={64} strokeWidth={1.5} />
                    <h3>Nenhuma meta definida ainda</h3>
                    <p>Crie sua primeira meta e comece a acompanhar seu desempenho!</p>
                    <button className="btn-create-first" onClick={() => setShowCreateModal(true)}>
                        <Plus size={18} />
                        Criar Primeira Meta
                    </button>
                </div>
            ) : (
                <div className="goals-grid">
                    {goals.map((goal) => (
                        <div
                            key={goal._id}
                            className="goal-card"
                            style={{ borderLeftColor: getGoalColor(goal.type) }}
                        >
                            <div className="goal-header">
                                <div className="goal-icon" style={{ background: `${getGoalColor(goal.type)}15`, color: getGoalColor(goal.type) }}>
                                    {getGoalIcon(goal.type)}
                                </div>
                                <div className="goal-actions">
                                    <button
                                        className="btn-icon"
                                        onClick={() => openEditModal(goal)}
                                        title="Editar"
                                    >
                                        <Edit2 size={16} />
                                    </button>
                                    <button
                                        className="btn-icon danger"
                                        onClick={() => handleDeleteGoal(goal._id)}
                                        title="Deletar"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>

                            <h3 className="goal-title">{getGoalLabel(goal.type)}</h3>

                            <div className="goal-values">
                                <div className="value-current">
                                    <span className="label">Atual</span>
                                    <span className="value">{formatValue(goal.type, goal.currentValue)}</span>
                                </div>
                                <div className="value-target">
                                    <span className="label">Meta</span>
                                    <span className="value">{formatValue(goal.type, goal.targetValue)}</span>
                                </div>
                            </div>

                            <div className="progress-container">
                                <div className="progress-bar">
                                    <div
                                        className="progress-fill"
                                        style={{
                                            width: `${goal.progress}%`,
                                            background: getGoalColor(goal.type)
                                        }}
                                    ></div>
                                </div>
                                <span className="progress-text">{goal.progress}%</span>
                            </div>

                            {goal.progress >= 100 && (
                                <div className="goal-achieved">
                                    <CheckCircle size={16} />
                                    Meta Atingida!
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Histórico */}
            {history.length > 0 && (
                <div className="goals-history">
                    <h3>Histórico de Metas</h3>

                    <div className="history-timeline">
                        {history.map((month, index) => (
                            <div key={`${month.year}-${month.month}`} className="history-month">
                                <div className="history-month-header">
                                    <Calendar size={18} />
                                    <span>{getMonthName(month.month)} {month.year}</span>
                                </div>

                                <div className="history-goals">
                                    {month.goals.map((goal, idx) => (
                                        <div key={idx} className="history-goal">
                                            <div className="history-goal-icon" style={{ color: getGoalColor(goal.type) }}>
                                                {getGoalIcon(goal.type)}
                                            </div>
                                            <div className="history-goal-info">
                                                <span className="history-goal-label">{getGoalLabel(goal.type)}</span>
                                                <span className="history-goal-value">
                                                    {formatValue(goal.type, goal.currentValue)} / {formatValue(goal.type, goal.targetValue)}
                                                </span>
                                            </div>
                                            <div className="history-goal-status">
                                                {goal.status === 'achieved' ? (
                                                    <span className="status-achieved">
                                                        <CheckCircle size={16} />
                                                        Atingida
                                                    </span>
                                                ) : (
                                                    <span className="status-failed">
                                                        <XCircle size={16} />
                                                        Não atingida
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Modal Criar Meta */}
            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Criar Nova Meta</h3>
                            <button className="btn-close" onClick={() => setShowCreateModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Tipo de Meta</label>
                                <div className="goal-types">
                                    <button
                                        className={`goal-type-btn ${goalType === 'revenue' ? 'active' : ''}`}
                                        onClick={() => setGoalType('revenue')}
                                        disabled={hasGoalOfType('revenue')}
                                    >
                                        <TrendingUp size={20} />
                                        Receita Total
                                    </button>
                                    <button
                                        className={`goal-type-btn ${goalType === 'commission' ? 'active' : ''}`}
                                        onClick={() => setGoalType('commission')}
                                        disabled={hasGoalOfType('commission')}
                                    >
                                        <DollarSign size={20} />
                                        Comissão
                                    </button>
                                    <button
                                        className={`goal-type-btn ${goalType === 'sales' ? 'active' : ''}`}
                                        onClick={() => setGoalType('sales')}
                                        disabled={hasGoalOfType('sales')}
                                    >
                                        <ShoppingCart size={20} />
                                        Volume de Vendas
                                    </button>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>
                                    {goalType === 'sales' ? 'Quantidade de Vendas' : 'Valor (R$)'}
                                </label>
                                <input
                                    type="number"
                                    step={goalType === 'sales' ? '1' : '0.01'}
                                    min="0"
                                    value={targetValue}
                                    onChange={(e) => setTargetValue(e.target.value)}
                                    placeholder={goalType === 'sales' ? 'Ex: 50' : 'Ex: 5000.00'}
                                    className="input-target"
                                />
                            </div>

                            <div className="info-box">
                                <AlertCircle size={16} />
                                <p>A meta será válida para o mês atual e será resetada automaticamente no dia 1º do próximo mês.</p>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowCreateModal(false)}>
                                Cancelar
                            </button>
                            <button className="btn-confirm" onClick={handleCreateGoal}>
                                Criar Meta
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Editar Meta */}
            {showEditModal && selectedGoal && (
                <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Editar Meta</h3>
                            <button className="btn-close" onClick={() => setShowEditModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Tipo de Meta</label>
                                <div className="goal-type-display">
                                    {getGoalIcon(selectedGoal.type)}
                                    <span>{getGoalLabel(selectedGoal.type)}</span>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>
                                    {selectedGoal.type === 'sales' ? 'Quantidade de Vendas' : 'Valor (R$)'}
                                </label>
                                <input
                                    type="number"
                                    step={selectedGoal.type === 'sales' ? '1' : '0.01'}
                                    min="0"
                                    value={targetValue}
                                    onChange={(e) => setTargetValue(e.target.value)}
                                    className="input-target"
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowEditModal(false)}>
                                Cancelar
                            </button>
                            <button className="btn-confirm" onClick={handleUpdateGoal}>
                                Salvar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VendorGoals;